import { useEffect, useState } from 'react';

import voiceService, { CallPermission } from '../services/voiceService';

/**
 * Stored WhatsApp call-permission state for the number about to be dialed.
 *
 * Owned by the calling dialog rather than by the hint that displays it, because two
 * things depend on it: the hint, and the default call method (an already-approved
 * patient should default to WhatsApp). Fetching it in one place keeps that to a
 * single request and one source of truth.
 *
 * A failed lookup resolves to "unknown" rather than staying null: not knowing and
 * knowing there is no approval lead to the same action — offer to send the request —
 * and leaving it null would hide the hint entirely.
 */
export function useWhatsAppCallPermission(params: { patientId: number; phone?: string | null; enabled?: boolean }) {
  const { patientId, phone, enabled = true } = params;
  const [permission, setPermission] = useState<CallPermission | null>(null);

  useEffect(() => {
    if (!enabled || !phone) {
      setPermission(null);
      return;
    }
    let stale = false;
    setPermission(null); // loading — do not report the previous number's state
    voiceService
      .getWhatsAppCallPermission({ patient_id: patientId, phone })
      .then((res) => {
        if (!stale) setPermission(res);
      })
      .catch(() => {
        if (!stale) {
          setPermission({ status: 'unknown', phone: phone ?? null, updated_at: null, valid_until: null, requested_at: null });
        }
      });
    return () => {
      stale = true;
    };
  }, [patientId, phone, enabled]);

  return { permission, setPermission, approved: permission?.status === 'accepted' };
}

export default useWhatsAppCallPermission;
